import { Component, OnInit, OnDestroy } from '@angular/core';
import { ForumService } from 'src/app/services/forum.service';
import Discussion from 'src/app/interfaces/Discussion';
import { UserService } from 'src/app/services/user.service';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { UploadService } from 'src/app/services/upload.service';
import { ResourceService } from 'src/app/services/resource.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { LoadingService } from 'src/app/services/loading.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { UrlService } from 'src/app/services/url.service';

@Component({
  selector: 'app-add-discussion',
  templateUrl: './add-discussion.component.html',
  styleUrls: ['./add-discussion.component.scss'],
})
export class AddDiscussionComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  currentUser;
  newDiscussion: Discussion = {
    attachment: null,
    approaches: [],
    comments: [],
    content: null,
    id: null,
    owner: null,
    ownerMeta: {
      name: null,
      institution: null,
      title: null,
    },
    prompt: null,
    reactions: [],
    tags: [],
    timestamp: null,
    resource: null,
  };
  attachmentFile: File = null;
  attachmentPreviewURL;
  titleFocused: false;
  quillModules = {
    toolbar: [['link', 'bold', 'italic', 'underline', 'strike', 'blockquote', { list: 'ordered' }, { list: 'bullet' }]],
  };
  enterBindings = [];
  step = 1;
  tags = {
    standard: [
      'technology',
      'environmental-impacts',
      'governance',
      'markets-finance',
      'funding',
      'cdr-community',
      'social-licensing',
    ],
    approaches: ['microalgae', 'blue-carbon', 'macroalgal','alkalinity', 'co2-removal', 'storage', 'other'],
  };
  resource = null;
  openAccessCheckbox = false;
  resourceImg;
  members = [];
  taggedMembers = [];

  constructor(
    private forumService: ForumService,
    private userService: UserService,
    private router: Router,
    private uploadService: UploadService,
    private route: ActivatedRoute,
    private resourceService: ResourceService,
    private notifications: NotificationsService,
    private loadingService: LoadingService,
    private analyticsService: AnalyticsService,
    private notificationService: NotificationsService,
    private urlService: UrlService
  ) {}

  ngOnInit() {
    this.analyticsService.event('add_discussion_view');
    this.subscriptions.add(
      this.userService.user.subscribe((u) => {
        if (u && u.uid) {
          this.currentUser = u;
        }
      })
    );

    // See if resource discussion
    this.route.queryParams.subscribe((params) => {
      if (params['resource']) {
        this.resourceService.getResource(params['resource']).then((r) => {
          this.resource = r;
          this.setResourceImg(this.resource);
          this.newDiscussion.resource = this.resource.id;
          if (this.resource.attachment) {
            this.newDiscussion.attachment = this.resource.attachment;
          }
          this.newDiscussion.approaches = this.resource.approaches;
          this.newDiscussion.tags = this.resource.tags;
        });
      }
    });
    this.subscriptions.add(this.userService.getMembers().subscribe((m) => (this.members = m)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  next(): void {
    if (this.step === 3 || this.resource) {
      this.loadingService.startLoading();
      this.newDiscussion.owner = this.currentUser.uid;
      if (!!!this.newDiscussion.content) {
        this.newDiscussion.content = '&nbsp;';
      }
      if (this.currentUser.displayName) {
        this.newDiscussion.ownerMeta.name = this.currentUser.displayName;
      }
      if (this.currentUser.title) {
        this.newDiscussion.ownerMeta.title = this.currentUser.title;
      }
      if (this.currentUser.institution) {
        this.newDiscussion.ownerMeta.institution = this.currentUser.institution;
      }
      this.newDiscussion.timestamp = new Date();
      this.newDiscussion.content = this.urlService.processContent(this.newDiscussion.content, this.taggedMembers);
      if (this.attachmentFile) {
        this.uploadService.uploadFile(this.attachmentFile).then((downloadUrl) => {
          this.newDiscussion.attachment = {
            name: this.attachmentFile.name,
            url: downloadUrl,
            size: this.attachmentFile.size,
          };
          this.createDiscussion();
        });
      } else {
        this.createDiscussion();
      }
    } else if (this.step === 1) {
      if (!!this.newDiscussion.content) {
        this.step++;
        this.analyticsService.event('add_discussion_click_next', { step: this.step });
      } else {
        alert('Description cannot be empty.');
      }
    } else {
      this.step++;
      this.analyticsService.event('add_discussion_click_next', { step: this.step });
    }
  }

  editorOpened(editor) {
    this.enterBindings = editor.quillEditor.keyboard.bindings[13].map((b) => Object.assign({}, b))
    delete editor.quillEditor.keyboard.bindings[13]
  }
  editorClosed(editor) {
    if (!editor.quillEditor.keyboard.bindings[13]) {
      editor.quillEditor.keyboard.bindings[13] = this.enterBindings.map((b) => Object.assign({}, b))
    }
  }

  back() {
    this.step--;
  }

  createDiscussion() {
    this.forumService.createDiscussion(this.newDiscussion).then(
      (id) => {
        const combinedTags = [...this.newDiscussion.approaches, ...this.newDiscussion.tags];
        // Add notification trigger to DB, let onCreate send them out.
        /*this.notifications.createNotificationTrigger({
          author: this.currentUser.uid,
          url: `/forum/${id}`,
          prompt: this.newDiscussion.prompt,
          content: this.newDiscussion.content,
          tags: combinedTags
        });*/
        this.notifications.sendNotifications({
          type: 'newDiscussions',
          author: this.currentUser.uid,
          url: `/forum/${id}`,
          prompt: this.newDiscussion.prompt,
          content: this.newDiscussion.content,
          tags: combinedTags
        });
        this.analyticsService.event('add_discussion_success', {
          approaches: this.newDiscussion.approaches,
          keywords: this.newDiscussion.tags,
        });
        if (this.taggedMembers.length > 0) {
          this.taggedMembers.forEach((tm) => {
            if (this.newDiscussion.content.indexOf('@' + tm.displayName) > -1) {
              this.notificationService.saveNotification(
                tm.uid,
                this.currentUser.uid,
                `mentioned you in a <a href="forum/${id}">discussion</a>`,
                this.currentUser.displayName,
                this.currentUser.profilePicture
              );
              this.notificationService.sendNotifications({
                type: 'directMentions',
                mention: tm.uid,
                comment: this.currentUser.displayName,
                url: `/forum/${id}`,
              });
            }
          });
        }
        this.taggedMembers = [];
        if (this.currentUser.comments) {
          this.currentUser.comments.push(id);
        } else {
          this.currentUser.comments = [id];
        }
        this.userService.saveUser(this.currentUser.uid, this.currentUser);
        this.loadingService.stopLoading();
        this.router.navigate(['/forum/' + id]);
      },
      (e) => console.error(e)
    );
  }

  upload(event): void {
    if (event.target && event.target.files.length > 0) {
      var reader = new FileReader();
      var t = this;
      reader.onload = function (e) {
        t.attachmentPreviewURL = reader.result;
      };
      if (event.target.files[0].name.endsWith('.pdf')) {
        let $img: any = document.querySelector('input[type="file"]');
        reader.readAsArrayBuffer($img.files[0]);
      }
      if (!event.target.files[0].name.endsWith('.pdf')) {
        reader.readAsDataURL(event.target.files[0]); // convert to base64 string
      }

      this.attachmentFile = event.target.files[0];
    }
  }
  removeFile() {
    if (this.attachmentFile) {
      this.attachmentFile = null;
    }
    if (this.newDiscussion.attachment) {
      this.newDiscussion.attachment = null;
    }
  }

  toggleApproach(a): void {
    if (this.newDiscussion.approaches.indexOf(a) === -1) {
      this.newDiscussion.approaches.push(a);
      this.analyticsService.event('add_discussion_add_approach', { approach: a });
    } else {
      this.newDiscussion.approaches.splice(this.newDiscussion.approaches.indexOf(a), 1);
    }
  }
  toggleTag(t): void {
    if (this.newDiscussion.tags.indexOf(t) === -1) {
      this.newDiscussion.tags.push(t);
      this.analyticsService.event('add_discussion_add_keyword', { approach: t });
    } else {
      this.newDiscussion.tags.splice(this.newDiscussion.approaches.indexOf(t), 1);
    }
  }

  setResourceImg(resource) {
    if (resource.resourceMeta) {
      if (resource.resourceMeta.image.charAt(0) === '/') {
        const url = new URL(resource.resourceUrl).origin;
        this.resourceImg = url + resource.resourceMeta.image;
      } else {
        this.resourceImg = resource.resourceMeta.image;
      }
    }
  }

  tagMember(m) {
    this.taggedMembers.push({ email: m.email, displayName: m.displayName, uid: m.uid });
  }

  globe() {
    return `
    <svg class="oc-icon globe" width="20" height="20" viewBox="0 0 20 20" fill="none"
    xmlns="http://www.w3.org/2000/svg">
      <path
        d="M19.9987 9.97312V9.97437C19.9937 8.05312 19.4387 6.26437 18.495 4.7425L18.4937 4.74125C18.0456 4.01875 17.51 3.35687 16.8981 2.77125C16.8825 2.75688 16.865 2.74187 16.8494 2.7275C16.5781 2.4725 16.2925 2.23438 15.995 2.01C15.9388 1.96875 15.8837 1.92562 15.8263 1.88562C14.8731 1.19937 13.7962 0.679375 12.6331 0.361875H12.6319C12.3013 0.27125 11.9631 0.199375 11.62 0.143125C11.6175 0.141875 11.615 0.141875 11.6125 0.141875C11.0862 0.05625 10.5506 0 10 0C8.11125 0 6.35125 0.53375 4.84375 1.44313L4.8425 1.44375C3.77062 2.0925 2.83062 2.93187 2.07125 3.92312C2.04687 3.955 2.0225 3.98688 1.99812 4.02C1.77875 4.315 1.5725 4.62125 1.38437 4.94125C1.36125 4.97937 1.33688 5.01687 1.315 5.05625C0.923125 5.7425 0.611875 6.47937 0.390625 7.25562C0.36125 7.35812 0.336875 7.46312 0.31125 7.56687C0.238125 7.85375 0.179375 8.14687 0.133125 8.44312C0.11625 8.55812 0.094375 8.67 0.080625 8.78625C0.033125 9.18437 0 9.58875 0 10C0 15.5225 4.47625 20 10 20C15.5225 20 20 15.5225 20 10C20 9.99125 19.9987 9.98312 19.9987 9.97312ZM18.1662 8.965C17.9637 8.755 17.6987 8.45 17.6794 8.15437C17.6637 7.65375 18.42 7.03 17.6244 6.69187C17.6719 6.47187 17.8012 6.41125 17.9487 6.36625C18.3675 7.27812 18.6337 8.27188 18.7156 9.31938C18.4556 9.21125 18.4081 9.24312 18.1662 8.965ZM11.4137 1.37437C11.4137 1.37437 11.6311 1.40262 11.7444 1.42562C11.7506 1.44 11.7633 1.42947 11.7444 1.42562C12.4525 1.56937 13.125 1.80625 13.76 2.11188C13.6613 2.14125 13.56 2.17062 13.4862 2.21187C14.6144 2.64375 13.0087 2.8175 12.7487 2.905C12.2287 3.07812 11.6219 3.685 11.1888 3.98875C10.7556 4.33562 10.2781 4.7675 9.97437 5.11562C9.585 5.54875 9.75813 6.15562 9.45562 6.58875C9.0225 6.19812 9.67188 5.76625 8.84875 5.67937C8.54375 5.67937 7.85 5.76625 7.5475 5.81C7.11312 5.89562 6.85437 6.06875 6.7675 6.58875C6.725 6.85 6.94062 7.63 7.11312 7.71688C7.59187 7.89 7.8075 7.02375 8.285 7.32625C8.63187 7.5875 8.11187 7.76062 8.11187 7.97687C7.9825 8.93 8.97875 9.27687 9.75875 9.58062C10.2788 8.84312 11.0163 8.75687 11.6656 9.23375C12.2537 9.23375 12.5519 9.75125 12.9631 10.0994L12.9669 10.1006C12.9744 10.1081 12.9825 10.1137 12.99 10.1213C12.995 10.125 12.9987 10.1288 13.0031 10.1319L12.9944 10.1256C13.5887 10.6406 14.1037 11.2756 14.7875 11.6175C15.265 11.92 16.5219 11.92 16.8675 12.4425C17.1713 12.8744 16.4344 14.0025 16.2606 14.5662C16.2181 14.825 16.1737 15.13 16.0006 15.345C15.8275 15.6919 15.5675 15.6919 15.3937 15.9087C15.0469 16.2556 14.83 16.8631 14.5269 17.2956C14.4694 17.3981 14.4087 17.4862 14.3475 17.58C13.4894 18.0744 12.5406 18.4262 11.5312 18.6056C11.4994 18.43 11.4581 18.2562 11.4481 18.0769C11.4044 17.3394 11.4481 16.6462 11.3175 15.9087C11.2306 15.5206 11.2306 14.9981 10.9706 14.7394C10.7106 14.3925 10.2331 14.5219 10.1038 14.0463C9.97313 13.6119 9.67062 13.3088 9.45438 12.9181C9.32375 12.6569 9.06375 12.0956 9.10625 11.79C9.15 11.4431 9.49687 11.27 9.58375 10.9231C9.88625 9.83937 9.15063 9.92688 8.5425 9.66562C7.93688 9.40562 7.93687 8.36562 7.2425 8.4525C6.50625 8.4525 5.725 7.975 5.33562 7.36875C4.98875 6.85062 4.94625 5.54937 4.46875 5.24625C4.46875 5.81 4.59937 6.33 4.68625 6.89313C4.1225 6.58938 4.1225 5.54937 3.90625 4.985C3.71687 4.53562 3.705 4.17938 3.78812 3.8475C5.375 2.245 7.57188 1.25 10 1.25C10.4819 1.25 11.4137 1.37437 11.4137 1.37437Z"
        fill="#2673CD" />
      </svg>
    `;
  }
}
